import { apiFetch } from './apiFetch';

const API_BASE = process.env.REACT_APP_API_URL;

export async function uploadFile(file) {
  // Force the file's bytes to be fully read into memory up front, rather
  // than handing fetch() the live File object to stream lazily as it sends
  // the request body. A File picked via a cloud-storage document picker
  // (e.g. Google Drive on Android) is backed by a content-provider handle,
  // not plain on-device bytes like a locally selected file - and reading
  // that handle mid-request has been observed to fail immediately, before
  // any data reaches the network, surfacing as a bare, contentless "Failed
  // to fetch" with no HTTP response at all. Reading it here instead, in a
  // context that gives back a real, catchable error, either avoids that
  // failure mode (the bytes are already resident by the time fetch() runs)
  // or at least turns it into a specific, diagnosable message instead of
  // the generic one.
  let fileForUpload = file;
  try {
    const buffer = await file.arrayBuffer();
    fileForUpload = new Blob([buffer], { type: file.type });
  } catch (err) {
    return { success: false, message: `Could not read the selected file: ${err.message || 'unknown error'}. Please try again, or pick the file from local storage instead of a cloud provider.` };
  }

  const formData = new FormData();
  formData.append('file', fileForUpload, file.name);

  let res;
  try {
    res = await apiFetch(`${API_BASE}/file/upload`, {
      method: 'POST',
      body: formData
    });
  } catch (err) {
    // fetch() itself throwing (as opposed to resolving with a non-2xx
    // response) means the request never completed at the network level -
    // e.g. the connection was interrupted mid-upload. Files picked via a
    // cloud-storage document picker (Google Drive on Android/iOS) are more
    // exposed to this than local files: the browser may still be streaming
    // the bytes in from the provider while the upload is in flight, rather
    // than attaching an already-resident file, so a flaky mobile connection
    // has more opportunity to break the request. err.message (e.g. "Failed
    // to fetch" / "Load failed") is surfaced so this is diagnosable instead
    // of the previous bare "File upload error" ManageCategory.js falls back
    // to when this throws uncaught.
    return { success: false, message: `Upload failed: ${err.message || 'network error'}. Please check your connection and try again.` };
  }

  try {
    return await res.json();
  } catch {
    // A non-JSON body (e.g. a reverse proxy's own HTML error page for a
    // request it rejected before the app ever saw it, like a 413 over its
    // body-size limit) would otherwise throw here and surface as an opaque
    // generic error to the caller.
    return { success: false, message: `Upload failed (HTTP ${res.status}).` };
  }
}

// Preview status codes carry a Vault-availability signal (503 VAULT_UNAVAILABLE
// vs any other failure) so the caller can show a reassuring, accurate message
// instead of a silent blank preview - previously this returned a bare
// url-or-null with no way to tell the two apart.
export async function fetchFileBlobUrl(filename) {
  const encodedFilename = encodeURIComponent(filename);
  const res = await apiFetch(`${API_BASE}/file/preview/${encodedFilename}`);
  if (!res.ok) {
    return { url: null, code: res.status === 503 ? 'VAULT_UNAVAILABLE' : 'ERROR' };
  }

  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), code: null };
}

// Same shape as fetchFileBlobUrl - previously this returned nothing on
// failure, so a download that failed because Vault was unreachable did
// nothing visible at all, with no way for the caller to tell the user why.
export async function downloadFileWithAuth(filename) {
  const encodedFilename = encodeURIComponent(filename);
  const res = await apiFetch(`${API_BASE}/file/download/${encodedFilename}`);
  if (!res.ok) {
    return { success: false, code: res.status === 503 ? 'VAULT_UNAVAILABLE' : 'ERROR' };
  }

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const contentDisposition = res.headers.get('Content-Disposition');
  let downloadName = filename.split('_').slice(1).join('_');
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match) downloadName = match[1];
  }

  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = downloadName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
  return { success: true, code: null };
}

export async function deleteFile(filename) {
  const encodedFilename = encodeURIComponent(filename);
  const res = await apiFetch(`${API_BASE}/file/delete/${encodedFilename}`, { method: 'DELETE' });
  return res.json();
}
